import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Eye, Pencil, CalendarDays } from 'lucide-react';
import { formatKurus } from '@yalidavet/shared';
import { api } from '@/lib/api';
import { formatDateTr } from '@/lib/utils';

const STATUS_TR: Record<string, string> = {
  draft: 'Taslak',
  pending_review: 'İncelemede',
  published: 'Yayında',
  paused: 'Duraklatıldı',
  rejected: 'Reddedildi',
};

const BOOKING_STATUS_TR: Record<string, string> = {
  pending: 'Onay Bekliyor',
  confirmed: 'Onaylandı',
  cancelled: 'İptal Edildi',
  completed: 'Tamamlandı',
};

export default function MyListingsPage() {
  const listings = useQuery({ queryKey: ['my-listings'], queryFn: () => api.myListings() });
  const bookings = useQuery({ queryKey: ['host-bookings'], queryFn: () => api.hostBookings() });

  return (
    <div className="yd-container space-y-10 py-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink-900">İlanlarım</h1>
          <p className="text-sm text-ink-500">Mülklerinizi ve rezervasyon taleplerinizi yönetin.</p>
        </div>
        <Link to="/ilanlarim/yeni" className="yd-btn-primary" data-testid="new-listing-cta">
          <Plus className="mr-1.5 h-4 w-4" /> Yeni İlan Ekle
        </Link>
      </header>

      {/* Listings */}
      <section data-testid="my-listings-grid">
        {listings.isLoading && <div className="yd-skeleton h-40" />}

        {listings.data?.items.length === 0 && (
          <div className="yd-card p-10 text-center" data-testid="my-listings-empty">
            <p className="mb-4 text-ink-500">Henüz bir ilanınız yok.</p>
            <Link to="/ilanlarim/yeni" className="yd-btn-primary">İlk İlanınızı Oluşturun</Link>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {listings.data?.items.map((m) => (
            <article key={m.id} className="yd-card overflow-hidden" data-testid={`my-listing-${m.id}`}>
              <img
                src={m.coverPhotoUrl ?? '/placeholder-mansion.jpg'}
                alt=""
                className="h-40 w-full object-cover"
              />
              <div className="space-y-2 p-4">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-semibold text-ink-900">{m.title}</h2>
                  <span className="yd-tag shrink-0" data-status={m.status}>
                    {STATUS_TR[m.status] ?? m.status}
                  </span>
                </div>
                <p className="text-sm text-ink-500">{m.district}, {m.city}</p>
                <p className="text-sm text-ink-600">
                  {m.maxCapacity} kişi · {m.gardenSizeM2} m² bahçe
                </p>
                <p className="font-semibold text-bosphorus-700">
                  {formatKurus(m.dailyPriceKurus)} <span className="text-xs font-normal text-ink-500">/ gece</span>
                </p>
                <div className="flex gap-2 pt-2">
                  <Link to={`/mulk/${m.slug}`} className="yd-btn-secondary flex-1 justify-center text-sm">
                    <Eye className="mr-1 h-4 w-4" /> Görüntüle
                  </Link>
                  <button type="button" className="yd-btn-ghost text-sm" aria-label="Düzenle">
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Incoming bookings */}
      <section>
        <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink-900">
          <CalendarDays className="h-5 w-5 text-bosphorus-600" /> Rezervasyon Talepleri
        </h2>

        {bookings.data?.items.length === 0 && (
          <p className="yd-card p-6 text-center text-sm text-ink-500">Henüz rezervasyon talebi yok.</p>
        )}

        <ul className="space-y-3" data-testid="host-bookings-list">
          {bookings.data?.items.map((b) => (
            <li key={b.id} className="yd-card flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-semibold text-ink-900">{b.mansionTitle}</p>
                <p className="text-sm text-ink-500">
                  {formatDateTr(b.checkinDate)} – {formatDateTr(b.checkoutDate)} · {b.guestCount} misafir
                </p>
                <p className="text-xs text-ink-400">{b.referenceCode}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-bosphorus-700">{formatKurus(b.totalKurus)}</p>
                <span className="yd-tag" data-status={b.status}>
                  {BOOKING_STATUS_TR[b.status] ?? b.status}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
